import type { FeaturedEventQuery, EventSearchQuery, PeatixEventResponse } from '../types/peatix-api';
import { mockEvents, delay } from './mock-events';
export { transformPeatixEvent } from './transform-event';

/**
 * Peatix API Base URL
 * 
 * Environment-specific configuration:
 * - Local: Uses mock data by default (set VITE_USE_MOCK_DATA=false to use real API)
 * - Staging/Production: Uses real API from environment variable or default
 * 
 * Set VITE_PEATIX_API_BASE in GitHub Actions secrets/variables for staging/production
 */
const PEATIX_API_BASE = import.meta.env.VITE_PEATIX_API_BASE || 'https://peatix-api.com/v4';

// Use mock data only if explicitly enabled (useful for local development when API has CORS issues)
// In staging/production, this should be false or undefined
const USE_MOCK_DATA = import.meta.env.VITE_USE_MOCK_DATA === 'true';

// Log which mode we're using
if (import.meta.env.DEV) {
  console.log('🔧 Development mode:', USE_MOCK_DATA ? 'Using mock data' : 'Using real API');
  console.log('🔧 API Base URL:', PEATIX_API_BASE);
}

/**
 * Fetch featured events from Peatix API
 * 
 * @param query - Featured event query parameters
 * @returns Promise with event data and pagination info
 * 
 * Example query:
 * {
 *   currentPage: 1,
 *   itemPerPage: 6
 * }
 */
export async function fetchFeaturedEvents(
  query: FeaturedEventQuery = {}
): Promise<PeatixEventResponse> {
  // Use mock data in development if enabled
  if (USE_MOCK_DATA) {
    console.log('🔧 Using mock data for featured events');
    await delay(500); // Simulate API delay
    return mockEvents;
  }

  const params = new URLSearchParams();
  
  if (query.currentPage) {
    params.append('currentPage', query.currentPage.toString());
  }
  if (query.itemPerPage) {
    params.append('itemPerPage', query.itemPerPage.toString());
  }
  
  // Add any additional query parameters
  Object.keys(query).forEach((key) => {
    if (key !== 'currentPage' && key !== 'itemPerPage' && query[key] !== undefined) {
      if (Array.isArray(query[key])) {
        query[key].forEach((val: any) => params.append(key, val.toString()));
      } else {
        params.append(key, query[key].toString());
      }
    }
  });

  const url = `${PEATIX_API_BASE}/events/featured?${params.toString()}`;
  
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch featured events: ${response.statusText}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching featured events:', error);
    throw error;
  }
}

/**
 * Search events from Peatix API
 * 
 * @param query - Event search query parameters
 * @returns Promise with event data and pagination info
 * 
 * Example query for Tokyo food events:
 * {
 *   currentPage: 1,
 *   itemPerPage: 6,
 *   countryCode: 'jp',
 *   type: 'physical',
 *   sort: 'datetime asc',
 *   tagIds: [123] // Optional: featured tag IDs
 * }
 */
export function buildEventSearchUrl(query: EventSearchQuery = {}): string {
  const params = new URLSearchParams();

  // Pagination
  if (query.currentPage) {
    params.append('currentPage', query.currentPage.toString());
  }
  if (query.itemPerPage) {
    params.append('itemPerPage', query.itemPerPage.toString());
  }

  // Search filters
  if (query.countryCode) {
    params.append('countryCode', query.countryCode);
  }
  if (query.type) {
    params.append('type', query.type);
  }

  // Sort: API expects "datetime asc" (with space, encoded as +)
  if (query.sort) {
    params.append('sort', query.sort);
  }

  // Peasy flag: always 0 (all events) for now.
  params.append('peasy', '0');

  // Multiple tagIds must be comma-separated for OR semantics.
  // Repeating tagIds as separate params is treated as AND by the API.
  if (query.tagIds) {
    if (Array.isArray(query.tagIds)) {
      const ids = query.tagIds.filter((id) => id !== null && id !== undefined);
      if (ids.length === 1) {
        params.set('tagIds', ids[0]!.toString());
      } else if (ids.length > 1) {
        params.set('tagIds', ids.join(','));
      }
    } else {
      params.set('tagIds', query.tagIds.toString());
    }
  }

  // Add any additional query parameters
  Object.keys(query).forEach((key) => {
    if (
      !['currentPage', 'itemPerPage', 'countryCode', 'type', 'sort', 'tagIds'].includes(key) &&
      query[key] !== undefined
    ) {
      if (Array.isArray(query[key])) {
        query[key].forEach((val: any) => params.append(key, val.toString()));
      } else {
        params.append(key, query[key].toString());
      }
    }
  });

  return `${PEATIX_API_BASE}/events/search?${params.toString()}`;
}

export async function searchEvents(
  query: EventSearchQuery = {}
): Promise<PeatixEventResponse> {
  // Use mock data in development if enabled
  if (USE_MOCK_DATA) {
    console.log('🔧 Using mock data for event search', query);
    await delay(500); // Simulate API delay
    return mockEvents;
  }

  const url = buildEventSearchUrl(query);
  
  console.log('🔍 Fetching events from:', url);
  
  try {
    const response = await fetch(url);
    
    console.log('📡 API Response status:', response.status, response.statusText);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ API Error Response:', errorText);
      throw new Error(`Failed to search events: ${response.status} ${response.statusText}. ${errorText}`);
    }
    
    const data = await response.json();
    console.log('✅ API Response data:', data);
    return data;
  } catch (error: any) {
    console.error('❌ Error searching events:', error);
    
    // Provide more helpful error messages
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      throw new Error(`Network error: Unable to reach API at ${PEATIX_API_BASE}. Check if the API endpoint is correct and CORS is configured.`);
    }
    
    throw error;
  }
}

